import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { forensicsApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'

type BackendResponse<T> = T | { success: boolean; data: T }

export const forensicsKeys = {
    all: ['forensics'] as const,
    environments: () => [...forensicsKeys.all, 'environments'] as const,
    environmentList: (params?: { vaultId?: number }) => [...forensicsKeys.environments(), params] as const,
    environment: (id: number) => [...forensicsKeys.all, 'environment', id] as const,
    versions: (envId: number) => [...forensicsKeys.all, 'versions', envId] as const,
    computeObjects: () => [...forensicsKeys.all, 'compute-objects'] as const,
}

export function useForensicsEnvironments(params?: { vaultId?: number }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: forensicsKeys.environmentList(params),
        queryFn: async () => {
            const response = await forensicsApi.listEnvironments(params)
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useForensicsEnvironment(id: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    return useQuery({
        queryKey: forensicsKeys.environment(id!),
        queryFn: async () => {
            const response = await forensicsApi.getEnvironment(id!)
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: !!id && isAuthenticated,
    })
}

export function useForensicsEnvironmentsByVault(vaultId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    return useQuery({
        queryKey: forensicsKeys.environmentList({ vaultId: vaultId! }),
        queryFn: async () => {
            const response = await forensicsApi.listEnvironments({ vaultId: vaultId! })
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: !!vaultId && isAuthenticated,
    })
}

export function useCreateForensicsEnvironment() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Parameters<typeof forensicsApi.createEnvironment>[0]) =>
            forensicsApi.createEnvironment(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Environment Created', description: 'Forensics environment has been created successfully.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useUpdateForensicsEnvironment() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Parameters<typeof forensicsApi.updateEnvironment>[1] }) =>
            forensicsApi.updateEnvironment(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environment(variables.id) })
            toast({ title: 'Environment Updated', description: 'Forensics environment has been updated successfully.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useDeleteForensicsEnvironment() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => forensicsApi.deleteEnvironment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Environment Deleted', description: 'Forensics environment has been deleted.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useStartForensicsVersion() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ envId, data }: { envId: number; data: { objectId: string; objectVersionId: number } }) =>
            forensicsApi.startVersion(envId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environment(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.versions(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Version Started', description: 'Object version has been submitted for forensics analysis.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useStopForensicsVersion() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ envId, versionId }: { envId: number; versionId: number }) =>
            forensicsApi.stopVersion(envId, versionId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environment(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.versions(variables.envId) })
            queryClient.invalidateQueries({ queryKey: forensicsKeys.environments() })
            toast({ title: 'Version Stopped', description: 'Forensics instance has been stopped.' })
        },
        onError: (error) => {
            toast({ title: 'Error', description: handleApiError(error), variant: 'destructive' })
        },
    })
}

export function useComputeObjects() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

    return useQuery({
        queryKey: forensicsKeys.computeObjects(),
        queryFn: async () => {
            const response = await forensicsApi.listComputeObjects()
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: isAuthenticated,
    })
}
